import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  assertNever,
  ProspectBookFilter,
  ProspectBookSort,
  ProspectMatchStatus,
  type AddToCallSheetResultDto,
  type ProspectBookActivity,
  type ProspectBookMatch,
  type CallSheetMembershipDto,
  type ProspectBookDto,
  type ProspectBookRowDto,
  type ProspectDto,
} from '@signalvote/shared';
import { Check, ChevronDown, Search, StickyNote, X } from 'lucide-react';
import { formatPhone } from '@/lib/phone';
import { Button } from '@/components/ui/button';
import { SelectCheckbox } from './SelectCheckbox';
import { SelectionBar, SelectionBarButton, SelectionBarDivider } from './SelectionBar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { filterLabel } from '@/lib/fundraising/bookFilters';
import { booksByPerson } from '@/lib/fundraising/callSheetAdd';
import { largestCandidateGift } from '@/lib/fundraising/matchVerdict';
import { BOOK_DEFAULT_SORT, nextSort, type ColumnSort } from '@/lib/fundraising/sorting';
import { useDelayedFlag } from '@/hooks/useDelayedFlag';
import { cn } from '@/lib/utils';
import { AddToCallSheetDialog } from './AddToCallSheetDialog';
import { LastActivityCell } from './LastActivityCell';
import { ProspectNoteEditor } from './ProspectNoteEditor';
import { LOADING_DELAY_MS, SortHeader, StaleRowsIndicator } from './SortHeader';
import { useSelectionKeys } from './useSelectionKeys';
import { useHoverIntent, type HoverIntent } from './useHoverIntent';
import { useWindowedRows } from './useWindowedRows';
import { ColumnWidths, STICKY_HEADING } from './WindowedTable';

// Asks and federal gifts are whole-dollar capacity signals: "$1,000", never "$1,437.50".
const dollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const money = (n: number | null | undefined) => (n == null ? '—' : dollars.format(n));
const count = new Intl.NumberFormat('en-US');

const joined = (separator: string, ...parts: (string | false | null | undefined)[]) =>
  parts.filter((part): part is string => !!part).join(separator);
const line = (...parts: (string | false | null | undefined)[]) => joined(' · ', ...parts);

type Counts = ProspectBookDto['counts'];

/** The segment control: who to show, by where the calling stands. Each counts within the other filters. */
const SEGMENTS: { filter: ProspectBookActivity | null; label: string; count: (counts: Counts) => number }[] = [
  { filter: null, label: 'Everyone', count: (c) => c.anyActivity },
  { filter: ProspectBookFilter.NeverCalled, label: 'Never called', count: (c) => c.neverCalled },
  { filter: ProspectBookFilter.CallBackOwed, label: 'Call-back', count: (c) => c.callBackOwed },
  { filter: ProspectBookFilter.OpenCommitment, label: 'Committed', count: (c) => c.openCommitment },
];

/** The Match filter's choices: what the book knows of a person. Each counts within the other filters. */
const MATCHES: { filter: ProspectBookMatch | null; count: (counts: Counts) => number }[] = [
  { filter: null, count: (c) => c.anyMatch },
  { filter: ProspectBookFilter.FederalGiving, count: (c) => c.federalGiving },
  { filter: ProspectBookFilter.PossibleMatch, count: (c) => c.possibleMatch },
  { filter: ProspectBookFilter.NoneFound, count: (c) => c.noneFound },
  { filter: ProspectBookFilter.NotMatchedYet, count: (c) => c.notMatchedYet },
  { filter: ProspectBookFilter.NoPhone, count: (c) => c.noPhone },
];

/** The book's own order: largest Ask first, then name. */
const byAskThenName = (a: ProspectBookRowDto, b: ProspectBookRowDto) =>
  (b.ask ?? -1) - (a.ask ?? -1) || a.prospect.name.localeCompare(b.prospect.name);

/** "1 is" / "3 are". */
const countIsAre = (n: number) => `${n} ${n === 1 ? 'is' : 'are'}`;

/** What an add did, as the server said it: added, anyone it still skipped and why, and the overlap warning. */
function addedNote(lead: string, result: AddToCallSheetResultDto) {
  const { skipped } = result;
  return line(
    lead,
    skipped.noPhone > 0 && `${skipped.noPhone} skipped: no phone`,
    skipped.doNotCall > 0 && `${skipped.doNotCall} skipped: asked not to be called`,
    skipped.alreadyOnSheet > 0 && `${countIsAre(skipped.alreadyOnSheet)} already on it`,
    result.alsoOnAnotherSheet > 0 && `${countIsAre(result.alsoOnAnotherSheet)} also on another call book`,
  );
}

const zoneAbbreviations = new Map<string, string | null>();

/** A zone as a caller reads it — "ET", "CT", "PT" — or null for none (or one this browser can't name). Named once per zone. */
function zoneAbbreviation(zone: string | null): string | null {
  if (!zone) return null;
  if (!zoneAbbreviations.has(zone)) {
    let name: string | null = null;
    try {
      name =
        new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortGeneric' })
          .formatToParts(new Date())
          .find((part) => part.type === 'timeZoneName')?.value ?? null;
    } catch {
      // An unknown zone reads as none.
    }
    zoneAbbreviations.set(zone, name);
  }
  return zoneAbbreviations.get(zone) ?? null;
}

/** Where they work and live, as one caption: "Professor · St. John's · Hoover, AL · CT". A blank occupation or
 *  employer reads as the server filled it from their FEC record, unmarked here. */
const personMeta = (row: ProspectBookRowDto) =>
  line(row.workLine.occupation.value, row.workLine.employer.value, joined(', ', row.prospect.city, row.prospect.state), zoneAbbreviation(row.timeZone));

/** A note written from the book's row: whose, what, and the token the row was read with. */
export interface RowNote {
  prospectId: string;
  notes: string;
  concurrencyVersion: number;
}

/** The last note save from a row: whose, whether it's still going, and why it was refused. */
export interface RowNoteSave {
  prospectId: string;
  saving: boolean;
  error: string | null;
}

const COLUMNS = 7;

export interface ProspectBookListProps {
  book: ProspectBookDto;
  /** The segment in use, or null for Everyone. */
  activity: ProspectBookActivity | null;
  /** The Match filter, or null for Any. */
  match: ProspectBookMatch | null;
  search: string;
  onActivityChange: (activity: ProspectBookActivity | null) => void;
  onMatchChange: (match: ProspectBookMatch | null) => void;
  onSearchChange: (search: string) => void;
  /** Open a person over the book, which stays where it was behind them. Keep it stable: every row takes it. */
  onOpen: (prospectId: string) => void;
  /** A person the pointer has rested on — likely to be opened next, so their page can be read ahead of time. */
  onHover?: (prospectId: string) => void;
  /** Add people to a call book; `onDone` gets what the add did. */
  onAddToSheet: (sheet: CallSheetMembershipDto, prospectIds: string[], onDone: (result: AddToCallSheetResultDto) => void) => void;
  /** Create a call book with people on it; `onDone` gets what the add did. */
  onCreateSheet: (name: string, prospectIds: string[], onDone: (result: AddToCallSheetResultDto) => void) => void;
  /** Why the last call book change was refused, as the server said it. */
  sheetError: string | null;
  /** The call book the list is narrowed to, or null for the whole book. It combines with the segment and Match. */
  sheetId: string | null;
  onSheetChange: (sheetId: string | null) => void;
  /** Matching is running, so people not matched yet are waiting their turn. */
  matching: boolean;
  /** The list, place and Ask filters, shown under the search; they combine with everything above. */
  moreFilters?: ReactNode;
  /** Those filters that are on, in words, for "no one matches". */
  narrowingNames?: string[];
  /** Turn those filters off — part of Clear. */
  onClearNarrowing?: () => void;
  /** Write a person's note from their row; `onSaved` closes the row's editor. Without it, rows offer no note. */
  onSaveNote?: (note: RowNote, onSaved: () => void) => void;
  /** The last note save from a row — only that row's editor shows it. */
  noteSave?: RowNoteSave | null;
  /** The column the server sorted these rows by; unset is the book's own order, largest Ask first, and null
   *  a call book's own order (no column marked). */
  sort?: ColumnSort<ProspectBookSort> | null;
  /** Sort by a column — the server sorts the whole book again; the rows here are never re-sorted. */
  onSortChange?: (next: ColumnSort<ProspectBookSort>) => void;
  /** The rows on screen are the last read's while the next one loads (a filter, search or sort changed). */
  stale?: boolean;
}

/**
 * The book's sortable columns, in order, after the checkbox, each with its fixed width (null takes the rest) —
 * wide enough for a phone number, the Match line's largest gift, an Ask with "if confirmed", two call book chips
 * and "Call back Fri, Sep 25"; anything longer truncates.
 */
const SORT_COLUMNS: { column: ProspectBookSort; label: string; width: string | null; className?: string }[] = [
  { column: ProspectBookSort.Name, label: 'Person', width: null },
  { column: ProspectBookSort.Phone, label: 'Phone', width: 'w-36' },
  { column: ProspectBookSort.Match, label: 'Match', width: 'w-48' },
  { column: ProspectBookSort.Ask, label: 'Suggested ask', width: 'w-36', className: 'text-right' },
  { column: ProspectBookSort.CallBooks, label: 'Call books', width: 'w-40' },
  { column: ProspectBookSort.LastActivity, label: 'Last activity', width: 'w-44' },
];
const COLUMN_WIDTHS = ['w-11', ...SORT_COLUMNS.map(({ width }) => width)];

/**
 * The Prospects book: the campaign's one list of Prospects — search, the segment control, the Match filter
 * and the call book filter, all of which combine — as a ledger of 56px two-line rows. A name (or anywhere on its
 * row) opens that person over the book, which keeps its scroll and selection behind them. Rows arrive sorted and filtered
 * by the server. Presentational.
 *
 * People are selected with a checkbox per row (or X on the row in focus), and the selection survives
 * filter and search changes — each selected person is remembered as last seen, so the ones a filter hides
 * can still be counted, shown and added. A bar at the bottom offers "Add to call book" (S) and Clear (Esc);
 * the add says who will be skipped and why before it's confirmed (ADR-089).
 */
export function ProspectBookList({
  book,
  activity,
  match,
  search,
  onActivityChange,
  onMatchChange,
  onSearchChange,
  onOpen,
  onHover,
  onAddToSheet,
  onCreateSheet,
  sheetError,
  sheetId,
  onSheetChange,
  matching,
  moreFilters,
  narrowingNames = [],
  onClearNarrowing,
  onSaveNote,
  noteSave = null,
  sort = BOOK_DEFAULT_SORT,
  onSortChange,
  stale = false,
}: ProspectBookListProps) {
  // Everyone selected, as last seen, in the order they were picked.
  const [selected, setSelected] = useState<Map<string, ProspectBookRowDto>>(() => new Map());
  const [showSelected, setShowSelected] = useState(false);
  const [adding, setAdding] = useState(false);
  const [notes, setNotes] = useState<string[]>([]);
  const { counts, total } = book;
  const today = getCampaignTodayString();
  const term = search.trim();
  const narrowed = activity !== null || match !== null || term !== '' || sheetId !== null || narrowingNames.length > 0;
  const filterNames = [
    activity && filterLabel(activity),
    match && filterLabel(match),
    term && `“${term}”`,
    ...narrowingNames,
  ].filter((part): part is string => !!part);
  const filtersNamed = filterNames.length > 1 ? `${filterNames.slice(0, -1).join(', ')} and ${filterNames[filterNames.length - 1]}` : filterNames[0];
  const viewedSheet = sheetId === null ? undefined : book.sheets.find((sheet) => sheet.id === sheetId);

  const current = useMemo(() => new Map(book.rows.map((row) => [row.prospect.id, row])), [book.rows]);
  const sheetsOf = useMemo(
    () => new Map([...booksByPerson(book.sheets)].map(([id, sheets]) => [id, sheets.map((sheet) => sheet.name)])),
    [book.sheets],
  );

  const showingSelected = showSelected && selected.size > 0;
  const selectedRows = useMemo(
    () => [...selected.keys()].map((id) => current.get(id) ?? selected.get(id)!),
    [selected, current],
  );
  const shownSelection = useMemo(() => (showingSelected ? [...selectedRows].sort(byAskThenName) : null), [showingSelected, selectedRows]);
  const rows = shownSelection ?? book.rows;
  const shownSelected = rows.filter((row) => selected.has(row.prospect.id)).length;
  const hiddenSelected = selected.size - shownSelected;
  const allShown = rows.length > 0 && shownSelected === rows.length;

  // Where the rows start moves with the toolbar and a note above them.
  const { listRef, bodyRef, measure, rows: windowedRows } = useWindowedRows(rows.length, [book, activity, match, sheetId, selected, showSelected, notes]);

  const changeActivity = (next: ProspectBookActivity | null) => {
    setShowSelected(false);
    onActivityChange(next);
  };
  const changeMatch = (next: ProspectBookMatch | null) => {
    setShowSelected(false);
    onMatchChange(next);
  };
  const clearFilters = () => {
    changeActivity(null);
    changeMatch(null);
    changeSearch('');
    if (sheetId !== null) changeSheet(null);
    onClearNarrowing?.();
  };
  const changeSearch = (next: string) => {
    setShowSelected(false);
    onSearchChange(next);
  };
  const changeSheet = (next: string | null) => {
    setShowSelected(false);
    onSheetChange(next);
  };
  const clearSelection = useCallback(() => {
    setSelected(new Map());
    setShowSelected(false);
  }, []);

  // Stable, so a tick or an open re-renders only the rows it changes.
  const toggleSelected = useCallback(
    (row: ProspectBookRowDto) =>
      setSelected((was) => {
        const next = new Map(was);
        if (next.has(row.prospect.id)) next.delete(row.prospect.id);
        else next.set(row.prospect.id, row);
        return next;
      }),
    [],
  );
  // Adds everyone shown to the selection, never replacing it; once they all are, unticks just them.
  const toggleAllShown = () =>
    setSelected((was) => {
      const next = new Map(was);
      for (const row of rows) {
        if (allShown) next.delete(row.prospect.id);
        else next.set(row.prospect.id, row);
      }
      return next;
    });

  // X selects the row in focus, S opens "Add to call book", Esc clears.
  useSelectionKeys({
    onToggle: (id) => {
      const row = current.get(id);
      if (row) toggleSelected(row);
    },
    onAdd: () => setAdding(true),
    onClear: clearSelection,
    hasSelection: selected.size > 0,
    paused: adding,
  });

  const hover = useHoverIntent(onHover);

  const renderRow = (row: ProspectBookRowDto, index: number) => (
    <BookRow
      key={row.prospect.id}
      row={row}
      index={index}
      measure={measure}
      isSelected={selected.has(row.prospect.id)}
      sheetNames={sheetsOf.get(row.prospect.id)}
      today={today}
      matching={matching}
      onOpen={onOpen}
      hover={hover}
      onToggleSelected={toggleSelected}
      onSaveNote={onSaveNote}
      noteSave={noteSave?.prospectId === row.prospect.id ? noteSave : null}
    />
  );

  const addNote = (note: string) => setNotes((was) => [...was, note]);

  // Stale rows are dimmed only after a moment, so a quick answer never flickers.
  const showStale = useDelayedFlag(stale, LOADING_DELAY_MS);
  // A header click waits on the server's re-sort; it's settled once a book arrives that isn't stale.
  const [sortClicked, setSortClicked] = useState<ProspectBookSort | null>(null);
  useEffect(() => {
    if (!stale) setSortClicked(null);
  }, [book, stale]);
  const changeSort = (column: ProspectBookSort) => {
    if (!onSortChange) return;
    setShowSelected(false);
    setSortClicked(column);
    onSortChange(nextSort(sort ?? BOOK_DEFAULT_SORT, column, BOOK_DEFAULT_SORT));
  };

  return (
    <div ref={listRef} className="space-y-4 pb-20">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => changeSearch(e.target.value)}
            placeholder="Search name, email or phone"
            aria-label="Search prospects"
            className="h-10 pl-9 text-[13px]"
          />
        </div>
        <div role="group" aria-label="Who to show" className="flex max-w-full gap-0.5 overflow-x-auto rounded-[10px] bg-muted p-[3px]">
          {SEGMENTS.map(({ filter: key, label, count: of }) => {
            const active = key === activity;
            return (
              <button
                key={label}
                type="button"
                aria-pressed={active}
                onClick={() => changeActivity(key)}
                className={cn(
                  'flex h-[38px] items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-xs font-semibold transition-colors',
                  active ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
                <span className="font-medium tabular-nums text-muted-foreground">{count.format(of(counts))}</span>
              </button>
            );
          })}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="h-10 text-[13px]">
              Match: {match ? filterLabel(match) : 'Any'}
              <ChevronDown className="ml-1.5 h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {MATCHES.map(({ filter: key, count: of }) => (
              <DropdownMenuItem key={key ?? 'any'} onSelect={() => changeMatch(key)} className="justify-between gap-6">
                {key ? filterLabel(key) : 'Any'}
                <span className="text-xs tabular-nums text-muted-foreground">{count.format(of(counts))}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="h-10 text-[13px]" disabled={book.sheets.length === 0 && !viewedSheet}>
              Call book: {viewedSheet?.name ?? 'Any'}
              <ChevronDown className="ml-1.5 h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
            <DropdownMenuItem onSelect={() => changeSheet(null)}>Any</DropdownMenuItem>
            {book.sheets.map((sheet) => (
              <DropdownMenuItem key={sheet.id} onSelect={() => changeSheet(sheet.id)}>
                {sheet.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <span className="ml-auto whitespace-nowrap text-xs tabular-nums text-muted-foreground">
          {narrowed ? (
            <>
              {count.format(total)} of {count.format(counts.everyone)} ·{' '}
              <button type="button" className="underline underline-offset-4" onClick={clearFilters}>
                Clear
              </button>
            </>
          ) : (
            `${count.format(counts.everyone)} · ${count.format(counts.readyToCall)} callable`
          )}
        </span>
      </div>

      {moreFilters}

      {sheetError && <p className="text-sm text-destructive">{sheetError}</p>}
      {notes.length > 0 && (
        <div role="status" className="flex items-center justify-between gap-2 rounded-lg border bg-card px-3 py-2 text-[13px] text-foreground">
          <span>
            <Check className="mr-1.5 inline h-4 w-4 text-success" />
            {notes.join(' · ')}
          </span>
          <button type="button" aria-label="Dismiss" className="grid h-8 w-8 place-items-center" onClick={() => setNotes([])}>
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      )}

      {/* Clipped, never a scroll box, so the headings pin to the page scroller (see WindowedTable). The columns'
          widths add up to its least width; a narrower window scrolls the page sideways. */}
      <div aria-busy={showStale} className="relative min-w-[68rem] overflow-clip rounded-xl border bg-card">
        <StaleRowsIndicator shown={showStale} />
        <table className="w-full table-fixed text-[13px]">
          <ColumnWidths widths={COLUMN_WIDTHS} />
          {/* Headings never wrap: a long row squeezing the columns would otherwise change the header's height. */}
          <thead className="whitespace-nowrap text-left text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            <tr className="h-10">
              <th className={cn('pl-3', STICKY_HEADING)}>
                <SelectCheckbox
                  checked={allShown ? true : shownSelected > 0 ? 'indeterminate' : false}
                  onCheckedChange={toggleAllShown}
                  disabled={rows.length === 0}
                  aria-label="Select everyone shown"
                />
              </th>
              {SORT_COLUMNS.map(({ column, label, className }) => (
                <SortHeader
                  key={column}
                  label={label}
                  column={column}
                  sort={sort}
                  onSort={changeSort}
                  disabled={!onSortChange}
                  pending={stale && sortClicked === column}
                  className={cn(className, STICKY_HEADING)}
                />
              ))}
            </tr>
          </thead>
          <tbody ref={bodyRef} className={cn('transition-opacity motion-reduce:transition-none', showStale && 'opacity-60')}>
            {windowedRows(COLUMNS, (index) => renderRow(rows[index], index))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS} className="px-3 py-10 text-center text-muted-foreground">
                  {viewedSheet ? (
                    filtersNamed ? (
                      <>
                        No one on {viewedSheet.name} matches {filtersNamed}.{' '}
                        <button
                          type="button"
                          className="underline"
                          onClick={() => {
                            changeActivity(null);
                            changeMatch(null);
                            changeSearch('');
                            onClearNarrowing?.();
                          }}
                        >
                          Clear filters
                        </button>
                      </>
                    ) : (
                      `No one on ${viewedSheet.name} yet. Select people from Everyone and add them to it.`
                    )
                  ) : narrowed ? (
                    <>
                      No one matches {filtersNamed}.{' '}
                      <button type="button" className="underline" onClick={clearFilters}>
                        Clear filters
                      </button>
                    </>
                  ) : (
                    'No prospects yet. Import a file to add the people you’ll call.'
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!showingSelected && total > book.rows.length && (
        <p className="text-sm text-muted-foreground">
          Showing the first {count.format(book.rows.length)} of {count.format(total)}. Search to find anyone else.
        </p>
      )}

      <SelectionBar
        count={selected.size}
        detail={hiddenSelected > 0 ? `${count.format(hiddenSelected)} not shown` : undefined}
      >
        {hiddenSelected > 0 && !showingSelected && (
          <SelectionBarButton size="sm" onClick={() => setShowSelected(true)}>
            Show selected
          </SelectionBarButton>
        )}
        {showingSelected && (
          <SelectionBarButton size="sm" onClick={() => setShowSelected(false)}>
            Back to the list
          </SelectionBarButton>
        )}
        <SelectionBarDivider />
        <SelectionBarButton primary shortcut="S" onClick={() => setAdding(true)}>
          Add to call book
        </SelectionBarButton>
        <SelectionBarButton shortcut="Esc" onClick={clearSelection}>
          Clear
        </SelectionBarButton>
      </SelectionBar>

      <AddToCallSheetDialog
        open={adding}
        onOpenChange={setAdding}
        selected={selectedRows}
        books={book.sheets}
        onAdd={(sheet, ids) => {
          setNotes([]);
          onAddToSheet(sheet, ids, (result) => addNote(addedNote(`Added ${result.added} to ${result.sheet.name}`, result)));
        }}
        onCreate={(name, ids) => {
          setNotes([]);
          onCreateSheet(name, ids, (result) => addNote(addedNote(`Created ${result.sheet.name} with ${result.added}`, result)));
        }}
      />
    </div>
  );
}

/**
 * One person's row: 56px, two lines. Memoised: every prop is stable unless this row changed, so ticking
 * one person or opening another re-renders just the rows involved, not the whole book.
 */
const BookRow = memo(function BookRow({
  row,
  index,
  measure,
  isSelected,
  sheetNames,
  today,
  matching,
  onOpen,
  hover,
  onToggleSelected,
  onSaveNote,
  noteSave,
}: {
  row: ProspectBookRowDto;
  index: number;
  /** The virtualizer's measuring ref, when the book is windowed. */
  measure: ((el: Element | null) => void) | undefined;
  isSelected: boolean;
  sheetNames: string[] | undefined;
  today: string;
  matching: boolean;
  onOpen: (prospectId: string) => void;
  hover: HoverIntent;
  onToggleSelected: (row: ProspectBookRowDto) => void;
  onSaveNote: ProspectBookListProps['onSaveNote'];
  /** This row's last note save; null when it was another row's. */
  noteSave: RowNoteSave | null;
}) {
  const { prospect } = row;
  const openPerson = () => onOpen(prospect.id);
  return (
    <tr
      ref={measure}
      data-index={index}
      data-prospect-id={prospect.id}
      className={cn('h-14 cursor-pointer border-b last:border-0 hover:bg-muted/40', isSelected && 'bg-primary/5')}
      onClick={openPerson}
      onMouseEnter={() => hover.enter(prospect.id)}
      onMouseLeave={hover.leave}
    >
      <td className="pl-3" onClick={(e) => e.stopPropagation()}>
        <SelectCheckbox checked={isSelected} onCheckedChange={() => onToggleSelected(row)} aria-label={`Select ${prospect.name}`} />
      </td>
      <td className="px-3 py-2">
        <span className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPerson();
            }}
            className="block min-w-0 max-w-full shrink truncate text-left text-[13px] font-semibold text-foreground hover:underline"
          >
            {prospect.name}
          </button>
          {onSaveNote && <RowNoteButton prospect={prospect} onSaveNote={onSaveNote} save={noteSave} />}
        </span>
        <p className="truncate text-xs text-muted-foreground">{personMeta(row)}</p>
      </td>
      <td className="truncate px-3 py-2 tabular-nums">
        {row.doNotCall ? (
          <span className="text-xs font-semibold text-destructive">Do Not Call</span>
        ) : prospect.phone?.trim() ? (
          formatPhone(prospect.phone)
        ) : (
          <span className="text-xs text-muted-foreground">No phone</span>
        )}
      </td>
      <td className="overflow-hidden px-3 py-2">
        <MatchCell row={row} matching={matching} />
      </td>
      <td className="overflow-hidden whitespace-nowrap px-3 py-2 text-right tabular-nums">
        <AskCell row={row} />
      </td>
      <td className="px-3 py-2">
        {/* One line: the chips share the column, each cut short rather than wrapping the row taller. */}
        <span className="flex gap-1 overflow-hidden">
          {sheetNames?.map((name) => (
            <span key={name} title={name} className="min-w-0 truncate rounded bg-muted px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground">
              {name}
            </span>
          ))}
        </span>
      </td>
      <td className="overflow-hidden px-3 py-2">
        <LastActivityCell activity={row.lastActivity} today={today} />
      </td>
    </tr>
  );
});


/**
 * A person's note from their row: their note's first line, cut to one line (the whole note lives on their
 * page), or just an icon when there's none — either opens the editor in place, without opening the person.
 */
function RowNoteButton({
  prospect,
  onSaveNote,
  save,
}: {
  prospect: ProspectDto;
  onSaveNote: NonNullable<ProspectBookListProps['onSaveNote']>;
  save: RowNoteSave | null;
}) {
  const [open, setOpen] = useState(false);
  const firstLine = prospect.notes?.trim().split('\n')[0]?.trim() || null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${firstLine ? 'Edit' : 'Add'} note for ${prospect.name}`}
          title={firstLine ? undefined : 'Add a note'}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'flex h-6 min-w-0 max-w-[14rem] shrink-[2] items-center gap-1 rounded px-1 text-[11px] hover:bg-muted',
            firstLine ? 'text-foreground' : 'text-muted-foreground/60',
          )}
        >
          <StickyNote aria-hidden="true" className={cn('h-3.5 w-3.5 shrink-0', firstLine && 'text-warning')} />
          {firstLine && <span className="truncate">{firstLine}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80" onClick={(e) => e.stopPropagation()}>
        <p className="mb-2 text-xs font-semibold text-muted-foreground">Note for {prospect.name}</p>
        <ProspectNoteEditor
          note={prospect.notes}
          autoFocus
          isSaving={save?.saving ?? false}
          error={save?.error ?? null}
          onCancel={() => setOpen(false)}
          onSave={(notes) =>
            onSaveNote({ prospectId: prospect.id, notes, concurrencyVersion: prospect.concurrencyVersion }, () => setOpen(false))
          }
        />
      </PopoverContent>
    </Popover>
  );
}

/** Where matching stands for a person, in words and a dot — status colour only for a real state. */
function MatchCell({ row, matching }: { row: ProspectBookRowDto; matching: boolean }) {
  const cellFor = (): { label: string; sub: string | null; dot: string; text: string } => {
    switch (row.matchStatus) {
      case ProspectMatchStatus.FederalGiving:
        return {
          label: 'Matched',
          // Both figures: the largest gift (what the Ask is sized from, perhaps held under it) and the total.
          sub: `Largest ${money(row.federalGiving?.largestSingleGift)} · ${money(row.federalGiving?.totalAcrossAcceptedIdentities)} federal`,
          dot: 'bg-success',
          text: 'text-success',
        };
      case ProspectMatchStatus.PossibleMatch:
        return { label: 'Review match', sub: `Largest ${money(largestCandidateGift(row))}`, dot: 'bg-warning', text: 'text-warning' };
      case ProspectMatchStatus.NoneFound:
        return { label: 'No match', sub: 'Not in federal records', dot: 'border border-muted-foreground', text: 'text-muted-foreground' };
      case ProspectMatchStatus.NotMatchedYet:
        return matching
          ? { label: 'Waiting', sub: null, dot: 'bg-border', text: 'text-muted-foreground' }
          : { label: 'Not matched yet', sub: null, dot: 'bg-border', text: 'text-muted-foreground' };
      default:
        return assertNever(row.matchStatus);
    }
  };
  const { label, sub, dot, text } = cellFor();
  return (
    <span className="block whitespace-nowrap">
      <span className={cn('flex items-center gap-1.5 text-xs font-semibold', text)}>
        <span aria-hidden="true" className={cn('h-2 w-2 shrink-0 rounded-full', dot)} />
        {label}
      </span>
      {sub && <span className="block truncate pl-3.5 text-xs text-muted-foreground">{sub}</span>}
    </span>
  );
}

/** The suggested ask: ink when it stands, slate "if confirmed" on a possible match, nothing before matching. */
function AskCell({ row }: { row: ProspectBookRowDto }) {
  if (row.ask != null) return <span className="font-semibold text-foreground">{money(row.ask)}</span>;
  if (row.matchStatus === ProspectMatchStatus.PossibleMatch) {
    return (
      <>
        <span className="block font-semibold text-muted-foreground">{money(largestCandidateGift(row))}</span>
        <span className="block text-xs text-muted-foreground">if confirmed</span>
      </>
    );
  }
  return (
    <>
      <span className="block text-muted-foreground">—</span>
      {row.matchStatus === ProspectMatchStatus.NotMatchedYet && <span className="block text-xs text-muted-foreground">after matching</span>}
    </>
  );
}
